import {
  Category,
  CategoryElement,
  CategoryItem,
  CategoryStatus,
  CategoryType,
} from "@/domain/category.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

export type CategoryPayload = {
  type?: CategoryType;
  status?: CategoryStatus;
  phoneNumber?: string;
  categories: CategoryItem[];
  createdBy?: string;
  updatedBy?: string;
};

const findByType = async ({
  type,
  status,
}: {
  type?: string;
  status: string;
}): Promise<Category> => {
  return axios
    .get("/categories/by-type", { params: { type } })
    .then((res) => {
      if (status === "ACTIVE" && res.data.body?.categories) {
        const categories = res.data.body.categories;

        return {
          ...res.data.body,
          categories: categories.filter(
            (category: CategoryElement) => category.status === status
          ),
        };
      }

      return res.data.body;
    })
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch categories"
      );
    });
};

const getTypes = async (): Promise<CategoryType[]> => {
  return axios
    .get("/categories/types")
    .then((res) => (res.data.body as CategoryType[]) || [])
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOneByType = async (type: CategoryType): Promise<Category | null> => {
  return axios
    .get("/categories/by-type", { params: { type } })
    .then((res) => (res.data.body as Category) || null)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: CategoryPayload): Promise<Category> => {
  return axios
    .post("/categories", data)
    .then((res) => res.data.body as Category)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: CategoryPayload;
}): Promise<Category> => {
  return axios
    .put(`/categories/${id}`, data)
    .then((res) => res.data.body as Category)
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const remove = async (id: string): Promise<void> => {
  return axios
    .delete(`/categories/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const CategoryService = {
  findByType,
  getTypes,
  findOneByType,
  create,
  update,
  remove,
};
